/**
 * Reads a `text/event-stream` body and hands each event's data to `onData`, in arrival order,
 * as soon as the event is complete. Resolves when the server closes the stream.
 *
 * <p>Only `data:` fields are read. A multi-line event's data is joined with newlines, as the
 * server-sent events format specifies; comments (`:`), `event:`, `id:` and `retry:` are
 * ignored, because nothing here reconnects or routes by event name.
 */
export async function readEventStream(
  stream: ReadableStream<Uint8Array>,
  onData: (data: string) => void,
): Promise<void> {
  const reader = stream.getReader();
  // `stream: true` keeps a character split across two chunks whole.
  const decoder = new TextDecoder();
  const lineBreak = /\r\n|\r|\n/;
  let buffer = '';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length === 0) return;
    const data = dataLines.join('\n');
    dataLines = [];
    onData(data);
  };

  const handleLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    if (field !== 'data') return;
    const value = colon === -1 ? '' : line.slice(colon + 1);
    dataLines.push(value.startsWith(' ') ? value.slice(1) : value);
  };

  let chunk = await reader.read();
  while (!chunk.done) {
    buffer += decoder.decode(chunk.value, { stream: true });
    let match = lineBreak.exec(buffer);
    // A lone "\r" at the very end may be the first half of a "\r\n" still on its way.
    while (match !== null && !(match[0] === '\r' && match.index === buffer.length - 1)) {
      handleLine(buffer.slice(0, match.index));
      buffer = buffer.slice(match.index + match[0].length);
      match = lineBreak.exec(buffer);
    }
    chunk = await reader.read();
  }
  buffer += decoder.decode();
  if (buffer !== '') handleLine(buffer);
  dispatch();
}
